const ENTRY_SELECTOR = '[data-legal-policy-entry]';
const ENTRY_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
const ENTRY_TRANSITIONS = Object.freeze({
  title: {
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 12px, 0)', filter: 'blur(7px)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
    ],
  },
  copy: {
    duration: 600,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 12px, 0)', filter: 'blur(7px)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0)', filter: 'blur(0px)' },
    ],
  },
});

const documentRoot = document.documentElement;
const policy = document.querySelector('[data-legal-policy-entry-root]');
const animations = [];
const SECTION_DELAY_START = 80;
const SECTION_DELAY_STEP = 60;
const SECTION_DELAY_MAX = 480;

const prepareSubscriptionBody = () => {
  if (!policy?.classList.contains('legal-policy--subscription')) {
    return;
  }

  const body = policy.querySelector('.legal-policy__body');
  const contentRoot = body?.children.length === 1 && body.firstElementChild?.tagName === 'DIV'
    ? body.firstElementChild
    : body;
  const paragraph = contentRoot?.children.length === 1 && contentRoot.firstElementChild?.tagName === 'P'
    ? contentRoot.firstElementChild
    : null;

  if (
    !paragraph?.querySelector('br') ||
    !paragraph.querySelector('strong') ||
    [...paragraph.children].some((element) => !['BR', 'STRONG', 'A', 'EM', 'B', 'I', 'SPAN'].includes(element.tagName))
  ) {
    return;
  }

  const lines = [];
  let line = document.createDocumentFragment();
  const finishLine = () => {
    if (line.textContent.trim()) {
      lines.push(line);
    }
    line = document.createDocumentFragment();
  };

  [...paragraph.childNodes].forEach((node) => {
    if (node.nodeName === 'BR') {
      finishLine();
    } else {
      line.append(node);
    }
  });
  finishLine();

  const sections = lines.map((content) => {
    const meaningfulNodes = [...content.childNodes].filter(
      (node) => node.nodeType !== Node.TEXT_NODE || node.textContent.trim(),
    );
    if (meaningfulNodes.length === 1 && meaningfulNodes[0].nodeName === 'STRONG') {
      const heading = document.createElement('h2');
      heading.textContent = meaningfulNodes[0].textContent.trim();
      return heading;
    }

    const copy = document.createElement('p');
    copy.append(content);
    return copy;
  });

  paragraph.replaceWith(...sections);
};

const prepareSectionEntries = () => {
  const body = policy?.querySelector('.legal-policy__body');

  if (!body) {
    return [];
  }

  const contentRoot =
    body.children.length === 1 && body.firstElementChild?.tagName === 'DIV'
      ? body.firstElementChild
      : body;

  contentRoot.querySelectorAll(':scope > p').forEach((paragraph) => {
    const text = paragraph.textContent.replace(/\s+/g, ' ').trim();
    const isShortStandaloneClause =
      text.length <= 140 &&
      (/^SECTION\s+\d+[A-Z]?\b/i.test(text) || /^(?:[IVXLCDM]+\.|\d+\.)\s+\S/i.test(text));

    if (isShortStandaloneClause) {
      paragraph.classList.add('legal-policy__section-heading');
    }
  });

  body.removeAttribute('data-legal-policy-entry');
  body.removeAttribute('data-legal-policy-entry-delay');

  let sectionIndex = 0;
  let hasEncounteredHeading = false;

  return [...contentRoot.children].filter((element) => element.textContent.trim()).map((element) => {
    const isHeading = element.matches(
      ':is(h2, h3, h4, .legal-policy__section-heading, p:has(> strong:only-child))',
    );

    if (isHeading) {
      sectionIndex = hasEncounteredHeading ? sectionIndex + 1 : 1;
      hasEncounteredHeading = true;
    }

    const delay = Math.min(
      SECTION_DELAY_START + sectionIndex * SECTION_DELAY_STEP,
      SECTION_DELAY_MAX,
    );

    element.dataset.legalPolicyEntry = 'copy';
    element.dataset.legalPolicyEntryDelay = String(delay);
    return element;
  });
};

const isInitiallyVisible = (element, viewportHeight, headerBottom) => {
  const bounds = element.getBoundingClientRect();

  return (
    bounds.width > 0 &&
    bounds.height > 0 &&
    bounds.right > 0 &&
    bounds.left < window.innerWidth &&
    bounds.bottom > headerBottom &&
    bounds.top < viewportHeight
  );
};

const completeEntry = () => {
  documentRoot.dataset.legalPolicyEntryState = 'complete';
  animations.splice(0).forEach((animation) => animation.cancel());
  policy
    ?.querySelectorAll(ENTRY_SELECTOR)
    .forEach((element) => element.classList.remove('is-legal-policy-entry-active'));
};

const startEntry = () => {
  if (documentRoot.dataset.legalPolicyEntryState !== 'pending') {
    return;
  }

  if (
    !policy ||
    window.Shopify?.designMode ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    typeof Element.prototype.animate !== 'function'
  ) {
    completeEntry();
    return;
  }

  const viewportHeight = window.innerHeight;
  const siteHeader = document.querySelector('.site-header');
  const headerBottom = siteHeader instanceof HTMLElement
    ? Math.max(0, siteHeader.getBoundingClientRect().bottom)
    : 0;
  const candidates = [...policy.querySelectorAll(ENTRY_SELECTOR)];

  candidates.forEach((element) => {
    if (!isInitiallyVisible(element, viewportHeight, headerBottom)) {
      return;
    }

    const transition = ENTRY_TRANSITIONS[element.dataset.legalPolicyEntry];

    if (!transition) {
      return;
    }

    const delay = Number.parseInt(element.dataset.legalPolicyEntryDelay || '0', 10);
    element.classList.add('is-legal-policy-entry-active');
    animations.push(
      element.animate(transition.keyframes, {
        delay: Number.isNaN(delay) ? 0 : delay,
        duration: transition.duration,
        easing: ENTRY_EASING,
        fill: 'both',
      }),
    );
  });

  documentRoot.dataset.legalPolicyEntryState = 'running';

  if (animations.length === 0) {
    completeEntry();
    return;
  }

  Promise.allSettled(animations.map((animation) => animation.finished)).then(completeEntry);
};

prepareSubscriptionBody();
prepareSectionEntries();
window.requestAnimationFrame(startEntry);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    completeEntry();
  }
});
